import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import { Seo } from "@/components/Seo";
import ProductCard from "@/components/ProductCard";
import NotFound from "@/pages/NotFound";
import { SampleBadge } from "@/pages/Portfolio";
import { serviceBySlug } from "@/data/services";
import { SITE_URL, breadcrumbSchema } from "@/lib/seo";

const Block = ({ label, children }) => (
  <div>
    <p className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-[#C87455] mb-3">{label}</p>
    <p className="text-zinc-300 leading-relaxed">{children}</p>
  </div>
);

export default function CaseStudy() {
  const { slug } = useParams();
  const [cs, setCs] = useState(undefined);
  const [products, setProducts] = useState([]);

  useEffect(() => {
    setCs(undefined);
    api.get(`/case-studies/${slug}`).then((r) => setCs(r.data)).catch(() => setCs(null));
  }, [slug]);

  useEffect(() => {
    if (!cs?.products?.length) return;
    api.get("/products").then((r) => setProducts(r.data.filter((p) => cs.products.includes(p.slug)))).catch(() => {});
  }, [cs]);

  if (cs === null) return <NotFound />;
  if (!cs) return <main className="pt-40 min-h-screen" />;

  const path = `/portfolio/${cs.slug}`;
  const services = (cs.services || []).map(serviceBySlug).filter(Boolean);

  return (
    <main data-testid={`case-study-page`} className="pt-40 pb-32 min-h-screen">
      <Seo
        title={`${cs.title} | Corporate Engraving Case Study — Ember & Steel Studio`}
        description={cs.summary}
        path={path}
        image={cs.cover}
        type="article"
        noindex={!!cs.sample}
        jsonLd={[
          { "@context": "https://schema.org", "@type": "CreativeWork", name: cs.title, description: cs.summary, image: cs.images, url: `${SITE_URL}${path}`, creator: { "@id": `${SITE_URL}/#business` }, locationCreated: cs.location || "Leduc, Alberta" },
          breadcrumbSchema([["Home", "/"], ["Portfolio", "/portfolio"], [cs.title, path]]),
        ]}
      />
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <nav aria-label="Breadcrumb" className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-zinc-500 flex gap-3">
          <Link to="/" className="hover:text-white">Home</Link><span>/</span>
          <Link to="/portfolio" className="hover:text-white">Portfolio</Link><span>/</span>
          <span className="text-zinc-300 truncate">{cs.title}</span>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mt-10">
          <div className="lg:col-span-7">
            <Reveal>
              <div className="flex flex-wrap items-center gap-3">
                <span className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-[#C87455]">{[cs.industry, cs.location].filter(Boolean).join(" · ")}</span>
                {cs.sample && <SampleBadge />}
              </div>
              <h1 className="font-serif-display font-light text-4xl sm:text-5xl lg:text-6xl text-white mt-4 tracking-tight leading-tight">{cs.title}</h1>
              <p className="text-zinc-300 text-base md:text-lg mt-6 leading-relaxed">{cs.summary}</p>
              <dl className="mt-8 grid grid-cols-2 sm:grid-cols-3 gap-4 font-mono-tech text-xs">
                {cs.client && <div><dt className="text-zinc-600 uppercase tracking-[0.2em] text-[10px]">Client</dt><dd className="text-zinc-300 mt-1">{cs.client}</dd></div>}
                {cs.quantity && <div><dt className="text-zinc-600 uppercase tracking-[0.2em] text-[10px]">Quantity</dt><dd className="text-zinc-300 mt-1">{cs.quantity}</dd></div>}
                {services.length > 0 && <div><dt className="text-zinc-600 uppercase tracking-[0.2em] text-[10px]">Services</dt><dd className="text-zinc-300 mt-1">{services.map((s) => s.nav).join(", ")}</dd></div>}
              </dl>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={0.1}>
              <div className="border border-zinc-800 overflow-hidden">
                {cs.cover && <img src={cs.cover} alt={cs.title} className="w-full aspect-square object-cover" data-testid="case-cover" />}
              </div>
            </Reveal>
          </div>
        </div>

        <Reveal>
          <section className="mt-20 grid grid-cols-1 md:grid-cols-3 gap-10" data-testid="case-body">
            {cs.challenge && <Block label="The brief">{cs.challenge}</Block>}
            {cs.solution && <Block label="What we made">{cs.solution}</Block>}
            {cs.result && <Block label="Delivered">{cs.result}</Block>}
          </section>
        </Reveal>

        {cs.images?.length > 1 && (
          <section className="mt-20 grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="case-gallery">
            {cs.images.slice(1).map((src, i) => <img key={src} src={src} alt={`${cs.title} — detail ${i + 1}`} loading="lazy" className="w-full aspect-[4/3] object-cover border border-zinc-900" />)}
          </section>
        )}

        {products.length > 0 && (
          <section className="mt-24" data-testid="case-products">
            <h2 className="font-serif-display text-3xl text-white mb-8">Products used</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">{products.map((p, i) => <ProductCard key={p.slug} product={p} index={i} />)}</div>
          </section>
        )}

        <section className="mt-24 border border-zinc-900 bg-[#0A0A0A] p-8 md:p-12 flex flex-col md:flex-row md:items-center justify-between gap-8">
          <div>
            <h2 className="font-serif-display text-3xl text-white">Want something like this for your team?</h2>
            <p className="text-zinc-400 text-sm mt-3 max-w-xl">Pick a package or describe the brief — we quote to your headcount and send a proof before engraving.</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to="/business" data-testid="case-business-cta" className="bg-[#C87455] text-black hover:bg-[#A65B3E] transition-colors px-6 py-3 font-mono-tech text-[10px] uppercase tracking-[0.2em] inline-flex items-center gap-2">Corporate packages <ArrowRight size={12} /></Link>
            <Link to="/portfolio" data-testid="case-back-link" className="border border-zinc-700 text-zinc-200 hover:border-[#C87455] transition-colors px-6 py-3 font-mono-tech text-[10px] uppercase tracking-[0.2em]">All case studies</Link>
          </div>
        </section>
      </div>
    </main>
  );
}
